import { FileDropzone } from '@winnin/ds-file-dropzone'
import { Icon } from '@winnin/ds-icon'
import { Loading } from '@winnin/winnin_ui.components.loading'
import { Modal } from '@winnin/winnin_ui.components.modal'
import { Title } from '@winnin/winnin_ui.components.title'

import { SearchInput } from 'components'
import IconButton from 'components/IconButton'
import { useTranslate } from 'lib/tolgee'
import { DocumentError, StorageProgressBar, UploadInputFile, UploadListItem, UploadLoading } from 'modules/documents'
import { FileUploadStatus } from 'modules/documents/common/contexts'
import { DeleteFileDialog } from 'modules/documents/delete-file-dialog'
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_TYPES } from 'services/documents/consts'

import { MODAL_STYLE } from './modal-documents.consts'
import { translations } from './modal-documents.i18n'
import { DocumentListSkeleton } from './modal-documents.skeleton'
import type { ModalDocumentsViewProps } from './modal-documents.types'
import { FILES_PER_DROP_LIMIT } from '../common/documents.consts'

import './modal-documents.styles.scss'

export const ModalDocumentsView = ({
  isOpen,
  onClose,
  documents,
  filteredDocuments,
  isUploading,
  uploadProgress,
  storageUsed,
  storageLimit,
  showDeleteConfirmation,
  onSearch,
  onDeleteDocument,
  onConfirmDelete,
  onCancelDelete,
  isHighlighted,
  onDragEnter,
  onDragLeave,
  onError,
  onButtonInfoClick,
  sentryRef,
  totalCount,
  isFetchingMore,
  isLoading,
  error,
  onRetryFetch,
  onButtonFilesClick,
  onDropFiles,
}: ModalDocumentsViewProps) => {
  const t = useTranslate()

  return (
    <>
      <Modal isOpen={isOpen} style={MODAL_STYLE} onRequestClose={onClose} shouldCloseOnOverlayClick>
        <div className="ModalDocuments">
          <header className="ModalDocuments-header">
            <div className="ModalDocuments-header-title-row">
              <Title tag="h3" className="ModalDocuments-header-title">
                {t(translations.modalTitle)}
              </Title>
              <IconButton
                color="inherit"
                icon={<Icon name="info" width={20} height={20} />}
                onClick={onButtonInfoClick}
              />
            </div>

            <IconButton
              icon={<Icon name="x" width={12} height={12} />}
              color="inherit"
              aria-label="Close modal"
              onClick={onClose}
            />
          </header>

          <div className="ModalDocuments-content">
            <div className="ModalDocuments-actions-row">
              <p className="ModalDocuments-actions-files-count">{t(translations.filesAdded(totalCount))}</p>
              <div className="ModalDocuments-actions-group">
                <SearchInput onSearch={onSearch} className="ModalDocuments-actions-search-input" />
                <UploadInputFile
                  id="ModalDocuments-actions-upload"
                  config={{
                    permissions: {
                      fileExtensions: ALLOWED_FILE_EXTENSIONS,
                      maxFilesPerDrop: FILES_PER_DROP_LIMIT,
                    },
                  }}
                  onUpload={onButtonFilesClick}
                  onError={error => onError(error.name)}
                >
                  {t(translations.addFilesButton)}
                </UploadInputFile>
              </div>
            </div>

            <FileDropzone
              classes={['ModalDocuments-dropzone']}
              isHighlight={isHighlighted}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDrop={onDropFiles}
              onError={error => onError(error.name)}
              config={{
                permissions: {
                  fileExtensions: ALLOWED_FILE_TYPES,
                  maxFilesPerDrop: FILES_PER_DROP_LIMIT,
                },
              }}
              overlay={{
                color: 'rgba(43, 43, 43, 0.80)',
                message: (
                  <div className="ModalDocuments-dropzone-overlay-content">
                    <Icon name="upload" width={20} height={20} />
                    <p>{t(translations.dropFilesMessage)}</p>
                  </div>
                ),
              }}
            >
              {error ? (
                <DocumentError onRetry={onRetryFetch} isLoading={isLoading} />
              ) : isLoading ? (
                <div className="ModalDocuments-list">
                  <DocumentListSkeleton />
                </div>
              ) : documents.length === 0 ? (
                <>
                  {!isHighlighted && (
                    <div className="ModalDocuments-dropzone-content">
                      <Icon name="upload" width={20} height={20} />
                      <p>{t(translations.emptyStateMessage)}</p>
                    </div>
                  )}
                </>
              ) : filteredDocuments.length === 0 ? (
                <div className="ModalDocuments-empty-search-content">
                  <Icon name="search" width={20} height={20} />
                  <p>{t(translations.emptySearchResultsMessage)}</p>
                </div>
              ) : (
                <>
                  <div className="ModalDocuments-list">
                    {filteredDocuments.map(document => (
                      <div key={`document-item-${document.id}`} className="ModalDocuments-listItem">
                        <UploadListItem
                          disabled={document.status === FileUploadStatus.Uploading}
                          fileExtension={document.fileExtension}
                          fileSize={document.size}
                          uploadDate={document.createdAt.toISOString()}
                          documentId={document.id}
                          onDelete={onDeleteDocument}
                          status={document.status}
                        >
                          {document.fileName}
                        </UploadListItem>
                      </div>
                    ))}
                    {isFetchingMore && <Loading />}
                    <div ref={sentryRef} style={{ height: '20px' }} />
                  </div>

                  <div className="ModalDocuments-statistics-row">
                    {isUploading ? (
                      <UploadLoading currentFile={uploadProgress.current} totalFiles={uploadProgress.total} />
                    ) : (
                      <div />
                    )}
                    <StorageProgressBar currentValue={storageUsed} maxValue={storageLimit} />
                  </div>
                </>
              )}
            </FileDropzone>
          </div>
        </div>
      </Modal>

      <DeleteFileDialog isOpen={showDeleteConfirmation} onClose={onCancelDelete} onConfirm={onConfirmDelete} />
    </>
  )
}
